/**
 * CacheControl — UI panel for managing offline tile cache.
 *
 * Provides buttons to cache the current viewport, cache the full NE MN
 * coverage area, clear the cache, and shows live progress + tile count.
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  isServiceWorkerSupported,
  registerServiceWorker,
  requestTileCache,
  clearTileCache,
  getCacheSize,
} from '../utils/cacheManager.js'

/**
 * Full NE Minnesota coverage area — predefined bounding box.
 * Roughly covers St. Louis, Lake, Cook, Itasca, Koochiching counties.
 */
const NE_MN_BOUNDS = { n: 48.7, s: 46.6, e: -89.5, w: -94.5 }

/**
 * Clamp a value to [min, max].
 */
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

export default function CacheControl({ mapBounds, zoom }) {
  const [open, setOpen] = useState(false)
  const [swStatus, setSwStatus] = useState('checking')
  const [cacheCount, setCacheCount] = useState(0)
  const [caching, setCaching] = useState(false)
  const [progress, setProgress] = useState({ loaded: 0, total: 0 })
  const [confirmClear, setConfirmClear] = useState(false)

  // Register SW and query initial cache size
  useEffect(() => {
    if (!isServiceWorkerSupported()) {
      setSwStatus('unsupported')
      return
    }

    registerServiceWorker().then((reg) => {
      if (reg) {
        setSwStatus('active')
        // Wait briefly for SW to become active before querying
        setTimeout(() => {
          getCacheSize()
            .then((count) => setCacheCount(count))
            .catch(() => {})
        }, 500)
      } else {
        setSwStatus('failed')
      }
    })
  }, [])

  // Refresh cache size periodically when panel is open
  useEffect(() => {
    if (!open || swStatus !== 'active') return
    const refresh = () => {
      getCacheSize()
        .then((count) => setCacheCount(count))
        .catch(() => {})
    }
    refresh()
    const interval = setInterval(refresh, 5000)
    return () => clearInterval(interval)
  }, [open, swStatus])

  const handleCacheCurrentView = useCallback(async () => {
    if (!mapBounds || caching) return
    const bounds = {
      n: mapBounds.n,
      s: mapBounds.s,
      e: mapBounds.e,
      w: mapBounds.w,
    }
    const zMin = clamp((zoom || 8) - 2, 6, 15)
    const zMax = clamp((zoom || 8) + 2, 6, 15)

    setCaching(true)
    setProgress({ loaded: 0, total: 0 })
    try {
      await requestTileCache(bounds, [zMin, zMax], (loaded, total) => {
        setProgress({ loaded, total })
      })
      const count = await getCacheSize()
      setCacheCount(count)
    } catch {
      // SW not ready
    }
    setCaching(false)
  }, [mapBounds, zoom, caching])

  const handleCacheArea = useCallback(async () => {
    if (caching) return
    setCaching(true)
    setProgress({ loaded: 0, total: 0 })
    try {
      await requestTileCache(NE_MN_BOUNDS, [6, 12], (loaded, total) => {
        setProgress({ loaded, total })
      })
      const count = await getCacheSize()
      setCacheCount(count)
    } catch {
      // SW not ready
    }
    setCaching(false)
  }, [caching])

  const handleClear = useCallback(async () => {
    if (!confirmClear) {
      setConfirmClear(true)
      return
    }
    try {
      await clearTileCache()
      setCacheCount(0)
    } catch {
      // ignore
    }
    setConfirmClear(false)
  }, [confirmClear])

  const progressPct =
    progress.total > 0
      ? Math.round((progress.loaded / progress.total) * 100)
      : 0

  const statusLabel =
    swStatus === 'active'
      ? 'SW Active'
      : swStatus === 'unsupported'
        ? 'SW Unsupported'
        : swStatus === 'failed'
          ? 'SW Failed'
          : 'Checking...'

  return (
    <>
      <button
        className="cache-control-toggle"
        type="button"
        onClick={() => { setOpen((v) => !v); setConfirmClear(false) }}
        title="Offline cache controls"
      >
        {open ? 'X' : 'Offline'}
      </button>

      {open && (
        <div className="cache-control">
          <div className="cache-status">
            <span>{statusLabel}</span>
            <span className="cache-count">{cacheCount} tiles cached</span>
          </div>

          {caching && (
            <div className="cache-progress">
              <div
                className="cache-progress-bar"
                style={{ width: `${progressPct}%` }}
              />
              <span className="cache-progress-text">
                {progress.loaded}/{progress.total}
              </span>
            </div>
          )}

          <button
            className="cache-btn"
            type="button"
            onClick={handleCacheCurrentView}
            disabled={caching || swStatus !== 'active'}
          >
            Cache Current View
          </button>

          <button
            className="cache-btn"
            type="button"
            onClick={handleCacheArea}
            disabled={caching || swStatus !== 'active'}
          >
            Cache NE MN Area
          </button>

          <button
            className="cache-btn cache-btn-danger"
            type="button"
            onClick={handleClear}
            disabled={caching || swStatus !== 'active'}
          >
            {confirmClear ? 'Confirm Clear' : 'Clear Cache'}
          </button>
        </div>
      )}
    </>
  )
}
